// The saved dashboards (the side panel's "Saved Notes" cards) — main.js's `savedPresets`,
// moved as-is in 05i-1. Each is a hand-arranged set of modules plus a Notes module with
// real, checkable findings about that exact data. `custom` is `_`-state createModule can't
// know about (an overlay comparison, a Heat Map's action, a note's text); since 05i-1 it's
// on the element before the module's first render (createModule's `custom`).
//
// Ben's own notes on these, kept from main.js:
// - Nothing in the prototype survives a reload, so these demo the real workflow (build a
//   dashboard, write down what you found, come back to it) without a backend.
// - "buurman-scouting" is transcribed from a real scouting-report screenshot: all three
//   radar names are real 25/26 centre-backs on the centre_back template, and the note text
//   is verbatim — including its stated age, which needn't match today's date.
// - `_noteContent` is HTML (the rich-text editor's innerHTML): <br> stands in for line
//   breaks, which a contenteditable div won't show for a bare "\n".

import type { PlayerId } from "../data/players.js";

export interface SavedPresetItem {
  title: string;
  col: number;
  row: number;
  /** The module's player, by id. */
  player?: PlayerId;
  custom?: Record<string, unknown>;
}

// The dashboards' players by id (ids-1) — a dashboard stores who, never a name (two players
// can share one). Names here only so the layouts below stay readable; the note texts are
// prose, not keys, and keep the names.
const MCCABE = 15620; // Katie Alison McCabe
const KERR = 4961; // Samantha May Kerr
const RUSSO = 47521; // Alessia Mia Teresa Russo
const BUURMAN = 402098; // Veerle Buurman
const KOGA = 423924; // Toko Koga
const SANGARE = 360541; // Wassa Sangaré

export interface SavedPreset {
  label: string;
  description: string;
  layout: SavedPresetItem[];
}

export const SAVED_PRESETS: Record<string, SavedPreset> = {
  "mccabe-crossing": {
    label: "Katie McCabe — Crossing & Set-Piece Threat",
    description: "Bio, Radar and Passing, with a note on her real chance-creation numbers this season.",
    layout: [
      { title: "Bio", col: 0, row: 0, player: MCCABE },
      { title: "Radar", col: 3, row: 0, player: MCCABE },
      { title: "Passing", col: 9, row: 0, player: MCCABE },
      {
        title: "Notes",
        col: 13,
        row: 0,
        custom: {
          _noteTitle: "Crossing & Set-Piece Threat",
          _noteContent:
            "McCabe has 1,276 real recorded passes this season (68.8/90, 78.3% success) — genuinely elite volume for a full back. 27 of those are key passes or assists (3 goal assists), so this isn’t just circulation, she’s directly creating chances from the left side. Worth checking the Radar’s crossing/OBV axes against the real full_back template pair (McCabe vs Huerta) to see how her end product actually compares positionally.",
        },
      },
    ],
  },
  "striker-radar-compare": {
    label: "Striker Radar Comparison — Kerr vs Russo",
    description: "A real overlay comparison (Radar + Attacking Stats) with a note on who’s actually outperforming this season.",
    layout: [
      {
        title: "Radar",
        col: 0,
        row: 0,
        player: KERR,
        custom: {
          _mergedPlayers: [KERR, RUSSO],
          _mergedColors: ["a", "b"],
          _mergedSeasons: ["52025", "52025"],
        },
      },
      {
        title: "Attacking",
        col: 6,
        row: 0,
        player: KERR,
        custom: {
          _mergedPlayers: [KERR, RUSSO],
          _mergedColors: ["a", "b"],
          _mergedSeasons: ["52025", "52025"],
        },
      },
      {
        title: "Notes",
        col: 10,
        row: 0,
        custom: {
          _noteTitle: "Kerr vs Russo: Who’s Outperforming?",
          _noteContent:
            "Kerr (Chelsea) is out-producing Russo (Arsenal) this season on pure output — 0.79 goals/90 vs 0.58 — and her underlying npxG/90 (0.575) is comfortably ahead of Russo’s (0.419) too, so it isn’t just finishing variance. Both sit on the real Striker radar template (13 shared axes), so the overlay makes it obvious where the two shapes actually diverge rather than just comparing two numbers in isolation.",
        },
      },
    ],
  },
  "mccabe-heatmap": {
    label: "Katie McCabe — Shot Location & Heat Map",
    description: "Heat Map + Shot Map, with a note on a real, checkable pattern in her shooting this season.",
    layout: [
      {
        title: "Heat Map",
        col: 0,
        row: 0,
        player: MCCABE,
        custom: { _heatmapAction: "shots", _heatmapStyle: "contour" },
      },
      { title: "Shot Map", col: 6, row: 0, player: MCCABE },
      {
        title: "Notes",
        col: 12,
        row: 0,
        custom: {
          _noteTitle: "Left-Footed Shot Profile",
          _noteContent:
            "20 real shots this season, exactly 1 goal — and every single one struck with her left foot (20 of 20). The Heat Map’s contour makes this visually obvious once you toggle Action to Shots: her attempts cluster down the inside-left channel, consistent with a left-footed full back cutting inside rather than crossing from the byline. Worth flipping the Action to Pass Origins next to see if her passing footprint tells the same story.",
        },
      },
    ],
  },
  "buurman-scouting": {
    label: "Veerle Buurman — Centre-Back Scouting Report",
    description: "Defending stats, ranking and a 3-way Radar overlay, with the real scouting note transcribed alongside it.",
    layout: [
      { title: "Defending", col: 0, row: 0, player: BUURMAN },
      {
        title: "Ranking",
        col: 4,
        row: 0,
        player: BUURMAN,
        custom: {
          _rankingCategoryId: "defending",
          _rankingMetricKey: "defensive_actions_90",
          _rankingSeason: "52025",
          _rankingSourcePlayer: BUURMAN,
        },
      },
      {
        title: "Radar",
        col: 7,
        row: 0,
        player: BUURMAN,
        custom: {
          _mergedPlayers: [BUURMAN, KOGA, SANGARE],
          _mergedColors: ["a", "b", "c"],
          _mergedSeasons: ["52025", "52025", "52025"],
        },
      },
      {
        title: "Notes",
        col: 0,
        row: 5,
        custom: {
          _noteTitle: "Veerle Buurman — Centre-Back Scouting",
          _noteContent:
            "Veerle Buurman<br>Chelsea | Centre-Back | Age 19<br><br>As a left-footed centre-back, she offers the ability to comfortably play on the left side of a back three or within a back four. Having developed in Dutch football, Buurman is comfortable on the ball and can step out to play passes forward, combining her defensive robustness with a progressive quality that suits Bompastor’s build-up preferences.",
        },
      },
    ],
  },
};
